import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { FixPlanCard, BuildPlanCard } from "./SmartEditFlow";
import DesignQuestionsCard from "./DesignQuestionsCard";
import RecipeSuggestionCard from "./RecipeSuggestionCard";
import "./MessageBubble.css";

/**
 * Split an assistant message into renderable parts:
 *   - { type: "text", content }       → prose / explanation (rendered via ReactMarkdown)
 *   - { type: "deployable", path, … } → [FILE: path] + code block (collapsed to a badge;
 *                                        user never needs to read the source)
 *   - { type: "code", lang, code }    → plain code block WITHOUT a [FILE:] marker (user
 *                                        needs to SEE or COPY this — terminal commands,
 *                                        scripts for their own machine, etc.)
 *
 * The distinction matters: deployable code is auto-saved to the container and is
 * visual noise in the chat, while instructional code is the ONLY way the user can
 * copy what they need to run elsewhere.
 */
export function splitMessageBlocks(text) {
  if (!text) return [];
  // Match either "[FILE: path]\n```…\n```" (deployable) or bare "```…\n```" (instructional)
  const re = /(\[FILE:\s*([\w./-]+)\]\s*\n)?```(\w*)\n([\s\S]*?)\n```/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    const [, fileHeader, path, lang, code] = match;
    if (fileHeader) {
      parts.push({ type: "deployable", path, lang, code });
    } else {
      parts.push({ type: "code", lang, code });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }
  return parts;
}

/**
 * Code block the user needs to see or copy. Renders the full source with a
 * copy button in the corner. Used for terminal commands, local scripts,
 * config examples — anything NOT deployed into the scaffold container.
 */
function InstructionalCodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        // Legacy fallback
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("copy failed:", err);
    }
  };
  return (
    <div className="instructional-code-block">
      <div className="instructional-code-header">
        {lang && <span className="instructional-code-lang">{lang}</span>}
        <button
          className={`instructional-code-copy ${copied ? "copied" : ""}`}
          onClick={handleCopy}
          aria-label="Copy code to clipboard"
        >
          {copied ? "✓ Copied!" : "Copy"}
        </button>
      </div>
      <pre className="instructional-code-pre">
        <code className={lang ? `language-${lang}` : undefined}>{code}</code>
      </pre>
    </div>
  );
}

/** Small badge shown in place of deployable ([FILE:] marked) code blocks. */
function DeployableBadge({ streaming }) {
  if (streaming) {
    return (
      <div className="code-working-badge">
        <span className="code-working-spinner" />
        <span>Building your integration...</span>
      </div>
    );
  }
  return (
    <div className="code-generated-badge">
      <span className="code-badge-icon">&#10003;</span>
      <span>Code generated</span>
    </div>
  );
}

/**
 * "You left off here last time" inline banner. Shown by handleEdit when
 * the user opens an app that has a persisted conversation with real
 * back-and-forth (> 2 turns). Replaces the removed sidebar History page —
 * puts the resume decision in context where it's most useful.
 *
 * The user can Resume (which rehydrates the full transcript via
 * handleLoadConversation) or Dismiss (which just removes this banner and
 * keeps the fresh-start chat). Either way they can start typing
 * immediately — the banner is non-blocking.
 */
function ResumeConversationBanner({ turnCount, updatedAt, onResume, onDismiss }) {
  const when = useMemo(() => {
    if (!updatedAt) return "earlier";
    const then = new Date(updatedAt).getTime();
    if (Number.isNaN(then)) return "earlier";
    const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(updatedAt).toLocaleDateString();
  }, [updatedAt]);

  return (
    <div className="resume-banner">
      <div className="resume-banner-body">
        <span className="resume-banner-icon">↺</span>
        <div className="resume-banner-text">
          <div className="resume-banner-title">Previous conversation available</div>
          <div className="resume-banner-meta">
            {turnCount} messages · last updated {when}
          </div>
        </div>
      </div>
      <div className="resume-banner-actions">
        <button className="resume-banner-dismiss" onClick={onDismiss} title="Start fresh (your old messages are still saved)">
          Dismiss
        </button>
        <button className="resume-banner-resume" onClick={onResume}>
          Resume
        </button>
      </div>
    </div>
  );
}

/** Inline-code + strong styling (shared across text segments). */
const INLINE_MARKDOWN_COMPONENTS = {
  code({ className, children, ...props }) {
    // Block code (language-xxx) usually won't reach here — we pre-split those
    // out as "deployable" or "code" parts. But mid-stream partials (unclosed
    // fences) can show up here; render them as a plain pre/code so they don't
    // look like garbled inline text.
    const isBlock = className && className.includes("language-");
    if (isBlock) {
      return (
        <pre className="instructional-code-pre"><code className={className}>{children}</code></pre>
      );
    }
    return <code className="inline-code" {...props}>{children}</code>;
  },
  // Let block-level code render through our own pre wrapper (handled above).
  pre({ children }) {
    return <>{children}</>;
  },
  strong({ children }) {
    const text = String(children);
    if (/^[A-Z][A-Z0-9_-]{2,}$/.test(text)) {
      return <strong className="api-key-name">{children}</strong>;
    }
    return <strong>{children}</strong>;
  },
};

export default function MessageBubble({ message, onAutoFix, onConfigureKeys, onOpenRecipe, onRebuild, onRecipeSuggestionUse, onRecipeSuggestionContinue, onFixPlanApprove, onFixPlanSkip, onBuildPlanApprove, onBuildPlanChange, onDesignQuestionsSubmit, onResumeConversation, onDismissResume, isSaved }) {
  const { role, content, _displayContent, _hasCode, _streaming, _checking, _autoFixAvailable, _deploying, _isFixPlan, _isBuildPlan, _isDesignQuestions, _isRecipeSuggestion, _recipeMatch, _isResumeBanner, _resumeConv, _resumeTurnCount, _resumeUpdatedAt, _layerBadge, _intermediateStep, _diagnosis } = message;
  // Strip the [FORGE_NAME: ...] tag from display
  const displayText = (_displayContent || content || "").replace(/\[FORGE_NAME:\s*[^\]]+\]\n?/g, "");

  // Pre-split into renderable parts so deployable blocks collapse to badges
  // while instructional blocks stay fully visible + copyable.
  const parts = useMemo(() => splitMessageBlocks(displayText), [displayText]);

  // Detect if Claude is telling the user to configure API keys — only show after app is saved
  const mentionsApiKeys = isSaved && role === "assistant" && !_streaming && !_deploying && !_checking &&
    displayText && /api key/i.test(displayText) && /panel|add|configure|set up/i.test(displayText);

  // Header-level layer badge — visible on any assistant message that ran
  // scope-limited (data-only or design-only). Makes it immediately obvious
  // when Claude's reply was served under a narrowed prompt rather than the
  // main one. Used to debug misroutes (e.g., Rob's "data layer assistant"
  // refusal could have been caught in one glance with this badge).
  const showHeaderLayerBadge =
    role === "assistant" &&
    _layerBadge &&
    (_layerBadge === "data" || _layerBadge === "design");

  return (
    <div className={`message message-${role}`}>
      <div className="message-label">
        {role === "user" ? "You" : role === "system" ? "System" : "Forge Studio"}
        {showHeaderLayerBadge && (
          <span
            className={`layer-badge layer-badge-${_layerBadge} layer-badge-header`}
            title={`This response was scoped to the ${_layerBadge} layer only — file output restricted to ${_layerBadge === "data" ? "routes/sources/*.js + config.js" : "views/index.html"}.`}
          >
            {_layerBadge === "data" ? "Data" : "Design"}
          </span>
        )}
      </div>
      <div className="message-body">
        {_isResumeBanner ? (
          <ResumeConversationBanner
            turnCount={_resumeTurnCount}
            updatedAt={_resumeUpdatedAt}
            onResume={() => onResumeConversation && onResumeConversation(message, _resumeConv)}
            onDismiss={() => onDismissResume && onDismissResume(message)}
          />
        ) : _isFixPlan ? (
          <FixPlanCard
            passes={message._fixPlanPasses}
            onApprove={() => onFixPlanApprove && onFixPlanApprove(message)}
            onSkip={() => onFixPlanSkip && onFixPlanSkip(message)}
            executing={message._fixPlanExecuting}
            currentPass={message._fixPlanCurrentPass}
          />
        ) : _isBuildPlan ? (
          <BuildPlanCard
            plan={message._buildPlan}
            onApprove={() => onBuildPlanApprove && onBuildPlanApprove(message)}
            onChange={() => onBuildPlanChange && onBuildPlanChange(message)}
          />
        ) : _isDesignQuestions ? (
          <DesignQuestionsCard
            questions={message._designQuestions}
            onSubmit={(answers) => onDesignQuestionsSubmit && onDesignQuestionsSubmit(message, answers)}
          />
        ) : _isRecipeSuggestion ? (
          <RecipeSuggestionCard
            match={_recipeMatch}
            onUseRecipe={(m) => onRecipeSuggestionUse && onRecipeSuggestionUse(message, m)}
            onContinue={() => onRecipeSuggestionContinue && onRecipeSuggestionContinue(message)}
          />
        ) : _checking ? (
          <div className="checking-indicator">
            <span className="code-working-spinner" />
            <span>Checking if everything is working...</span>
          </div>
        ) : (
          // Render parts in order. Deployable → badge. Instructional → visible + copyable.
          // Prose segments → ReactMarkdown (inline code still styled; strong/emphasis work).
          <>
            {parts.map((part, i) => {
              if (part.type === "deployable") {
                return <DeployableBadge key={i} streaming={_streaming} />;
              }
              if (part.type === "code") {
                return <InstructionalCodeBlock key={i} lang={part.lang} code={part.code} />;
              }
              return (
                <ReactMarkdown key={i} components={INLINE_MARKDOWN_COMPONENTS}>
                  {part.content}
                </ReactMarkdown>
              );
            })}
          </>
        )}
        {_streaming && !displayText && (
          <div className="streaming-indicator">
            <span className="code-working-spinner" />
            <span>Thinking — this can take up to a minute for complex requests...</span>
          </div>
        )}
        {_streaming && displayText && (
          <div className="streaming-indicator">
            <span className="streaming-dot" />
            <span>Still working...</span>
          </div>
        )}
        {_deploying && (
          <div className="code-working-badge">
            <span className="code-working-spinner" />
            <span>Deploying preview...</span>
          </div>
        )}
        {_intermediateStep && !_streaming && (
          <div className="code-generated-badge" style={{ background: "rgba(59, 130, 246, 0.1)", borderColor: "rgba(59, 130, 246, 0.3)", color: "#60a5fa" }}>
            <span className="code-badge-icon">&#10003;</span>
            <span>Data layer ready — building design next...</span>
            {_layerBadge && (
              <span className={`layer-badge layer-badge-${_layerBadge}`}>
                {_layerBadge === "data" ? "Data" : _layerBadge === "design" ? "Design" : ""}
              </span>
            )}
          </div>
        )}
        {_hasCode && !_streaming && !_deploying && !_intermediateStep && (
          <div className="code-generated-badge">
            <span className="code-badge-icon">&#10003;</span>
            <span>Preview is live — check the right panel</span>
            {_layerBadge && (
              <span className={`layer-badge layer-badge-${_layerBadge}`}>
                {_layerBadge === "data" ? "Data updated" : _layerBadge === "design" ? "Design updated" : "Updated"}
              </span>
            )}
          </div>
        )}
        {mentionsApiKeys && onConfigureKeys && (
          <button className="configure-keys-btn" onClick={onConfigureKeys}>
            <span>🔑</span>
            Configure API Keys Now
          </button>
        )}
        {_diagnosis && _diagnosis.suggestedAction === "rebuild" && onRebuild && (
          <button className="rebuild-btn" onClick={onRebuild}>
            <span>↺</span>
            Rebuild
          </button>
        )}
        {/* Classifier linked this error to a setup-guide recipe — render a jump-to-docs button */}
        {_diagnosis && _diagnosis.recipeId && onOpenRecipe && (
          <button
            className="recipe-link-btn"
            onClick={() => onOpenRecipe(_diagnosis.recipeId, _diagnosis.docsAnchor)}
          >
            <span>📖</span>
            See the setup guide →
          </button>
        )}
        {_autoFixAvailable && onAutoFix && (
          <button
            className="autofix-btn"
            onClick={() => onAutoFix(message._fixName, message._fixError, message._fixLogs, message._fixPayload)}
          >
            <span className="autofix-icon">&#9881;</span>
            Auto-fix this issue
          </button>
        )}
      </div>
    </div>
  );
}
